import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AIUsageWidget, ComplianceAuditWidget, BulkSubscriberActions } from '../components/AdminWidgets';
import TaxOverview from '../components/TaxOverview';
import OwnerNotesPanel from '../components/owner/OwnerNotesPanel';
import InternalAccountsPanel from '../components/owner/InternalAccountsPanel';
import TestAccountsPanel from '../components/owner/TestAccountsPanel';
import LicensesPanel from '../components/owner/LicensesPanel';
import IPPanel from '../components/owner/IPPanel';
import HowToGuide from '../components/owner/HowToGuide';
import PortalHierarchyPanel from '../components/owner/PortalHierarchyPanel';
import TenantAnalytics from '../components/TenantAnalytics';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  Search,
  Edit,
  Power,
  PowerOff,
  Shield,
  UserCheck,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Zap,
  Crown,
  Rocket,
  RefreshCw,
  UserPlus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Package,
  Calculator,
  Truck,
  FileText,
  Calendar,
  Mail,
  Phone,
  Globe,
  X,
  Save,
  Eye,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module configuration with parent-child hierarchy
const MODULE_HIERARCHY = {
  core: {
    name: 'Core',
    icon: Package,
    color: 'bg-emerald-100 text-emerald-700',
    description: 'Essential business features',
    children: [
      { id: 'invoicing', name: 'Invoicing' },
      { id: 'clients', name: 'Clients' },
      { id: 'services_goods', name: 'Services & Goods' },
      { id: 'basic_reports', name: 'Basic Reports' },
      { id: 'email_invoice', name: 'Email Invoice' },
    ],
  },
  hr: {
    name: 'HR',
    icon: Users,
    color: 'bg-blue-100 text-blue-700',
    description: 'Human resources management',
    children: [
      { id: 'employees', name: 'Employees' },
      { id: 'time_tracking', name: 'Time Tracking' },
      { id: 'portal_management', name: 'Portal Management' },
      { id: 'training', name: 'Training' },
      { id: 'unions', name: 'Unions' },
    ],
  },
  operations: {
    name: 'Operations',
    icon: Truck,
    color: 'bg-purple-100 text-purple-700',
    description: 'Fleet and equipment management',
    children: [
      { id: 'fleet', name: 'Fleet' },
      { id: 'equipment', name: 'Equipment' },
    ],
  },
  payroll: {
    name: 'Payroll',
    icon: Calculator,
    color: 'bg-amber-100 text-amber-700',
    description: 'Payroll and tax management',
    children: [
      { id: 'payroll', name: 'Payroll' },
      { id: 'tax_settings', name: 'Tax Settings' },
    ],
  },
  compliance: {
    name: 'Compliance',
    icon: Shield,
    color: 'bg-red-100 text-red-700',
    description: 'Safety and policy management',
    children: [
      { id: 'safety', name: 'Safety' },
      { id: 'policies', name: 'Policies' },
      { id: 'certifications', name: 'Certifications' },
      { id: 'audit_logs', name: 'Audit Logs' },
    ],
  },
  documents: {
    name: 'Documents',
    icon: FileText,
    color: 'bg-cyan-100 text-cyan-700',
    description: 'Document management',
    children: [
      { id: 'documents', name: 'Documents' },
    ],
  },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-500', icon: CheckCircle },
  trialing: { label: 'Trial', color: 'bg-blue-500', icon: Clock },
  past_due: { label: 'Past Due', color: 'bg-amber-500', icon: AlertTriangle },
  canceled: { label: 'Canceled', color: 'bg-red-500', icon: XCircle },
  incomplete: { label: 'Incomplete', color: 'bg-gray-500', icon: Clock },
};

const PLAN_CONFIG = {
  starter: { label: 'Starter', color: 'bg-blue-500', icon: Zap, price: 14 },
  professional: { label: 'Professional', color: 'bg-violet-500', icon: Crown, price: 34 },
  enterprise: { label: 'Enterprise', color: 'bg-amber-500', icon: Rocket, price: 69 },
  modular: { label: 'Modular', color: 'bg-cyan-500', icon: Package, price: 0 },
  legacy: { label: 'Legacy', color: 'bg-gray-500', icon: Shield, price: 0 },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'analytics';
  const setActiveTab = (val) => {
    setSearchParams(val === 'analytics' ? {} : { tab: val });
  };
  const [loading, setLoading] = useState(true);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');

  // Edit subscriber dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});

  const [editForm, setEditForm] = useState({
    subscription_plan: '',
    subscription_status: '',
    is_active: true,
    trial_days: 14,
    enabled_modules: [],
  });

  // Delete user dialog
  const [deleteUserDialog, setDeleteUserDialog] = useState(null);

  // Create admin dialog
  const [createAdminDialogOpen, setCreateAdminDialogOpen] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({
    email: '',
    password: '',
    name: '',
  });

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const accessCheck = await fetch(`${API_URL}/api/admin/access-check`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!accessCheck.ok) {
        setHasAdminAccess(false);
        setLoading(false);
        return;
      }

      const accessData = await accessCheck.json();
      setHasAdminAccess(accessData.has_admin_access);
      setIsMaster(accessData.is_master);

      if (!accessData.has_admin_access) {
        setLoading(false);
        return;
      }

      const [statsRes, tenantsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/admin/tenants`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (tenantsRes.ok) setTenants(await tenantsRes.json());

      if (accessData.is_master) {
        const adminsRes = await fetch(`${API_URL}/api/admin/admins`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (adminsRes.ok) setAdmins(await adminsRes.json());
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchTenantUsers = async (tenantId) => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenantId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTenantUsers(data);
      }
    } catch (error) {
      console.error('Error fetching tenant users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleEditTenant = async (tenant) => {
    setSelectedTenant(tenant);

    // Calculate trial days remaining
    let trialDays = 14;
    if (tenant.trial_ends_at) {
      const trialEnd = new Date(tenant.trial_ends_at);
      const now = new Date();
      const diffTime = trialEnd - now;
      trialDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    }

    setEditForm({
      subscription_plan: tenant.subscription_plan || 'starter',
      subscription_status: tenant.subscription_status || 'incomplete',
      is_active: tenant.is_active !== false,
      trial_days: trialDays,
      enabled_modules: tenant.enabled_modules || [],
    });

    // Expand all modules by default
    const expanded = {};
    Object.keys(MODULE_HIERARCHY).forEach((key) => {
      expanded[key] = true;
    });
    setExpandedModules(expanded);

    setEditDialogOpen(true);
    await fetchTenantUsers(tenant.id);
  };

  const handleSaveTenant = async () => {
    if (!selectedTenant) return;

    setSavingChanges(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscription_plan: editForm.subscription_plan,
          subscription_status: editForm.subscription_status,
          is_active: editForm.is_active,
          trial_days: editForm.trial_days,
          enabled_modules: editForm.enabled_modules,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update subscriber');
      }

      toast.success('Subscriber updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserDialog) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${deleteUserDialog.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }

      toast.success('User deleted successfully');
      setDeleteUserDialog(null);
      if (selectedTenant) {
        fetchTenantUsers(selectedTenant.id);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleQuickAction = async (tenantId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenantId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `Failed to ${action} tenant`);
      }

      toast.success(`Subscriber ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdminForm.email || !newAdminForm.password || !newAdminForm.name) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newAdminForm),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create admin');
      }

      toast.success('Admin account created');
      setCreateAdminDialogOpen(false);
      setNewAdminForm({ email: '', password: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!confirm('Are you sure you want to delete this admin account?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/admins/${adminId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete admin');
      }

      toast.success('Admin account deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Module toggle functions
  const toggleModule = (moduleKey) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }));
  };

  const isModuleEnabled = (moduleKey) => {
    return editForm.enabled_modules?.includes(moduleKey);
  };

  const isFeatureEnabled = (featureId) => {
    return editForm.enabled_modules?.includes(featureId);
  };

  const toggleParentModule = (moduleKey) => {
    const module = MODULE_HIERARCHY[moduleKey];
    const childIds = module.children.map((c) => c.id);
    const allChildrenEnabled = childIds.every((id) => editForm.enabled_modules?.includes(id));

    if (allChildrenEnabled) {
      // Disable all children
      setEditForm((prev) => ({
        ...prev,
        enabled_modules: prev.enabled_modules.filter((id) => !childIds.includes(id) && id !== moduleKey),
      }));
    } else {
      // Enable all children
      const newModules = new Set([...editForm.enabled_modules, moduleKey, ...childIds]);
      setEditForm((prev) => ({
        ...prev,
        enabled_modules: Array.from(newModules),
      }));
    }
  };

  const toggleFeature = (featureId, moduleKey) => {
    const isEnabled = editForm.enabled_modules?.includes(featureId);

    if (isEnabled) {
      setEditForm((prev) => ({
        ...prev,
        enabled_modules: prev.enabled_modules.filter((id) => id !== featureId),
      }));
    } else {
      setEditForm((prev) => ({
        ...prev,
        enabled_modules: [...prev.enabled_modules, featureId],
      }));
    }
  };

  const filteredTenants = tenants.filter((tenant) => {
    const matchesSearch =
      !searchQuery ||
      tenant.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.owner_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || tenant.subscription_status === filterStatus;
    const matchesPlan = filterPlan === 'all' || tenant.subscription_plan === filterPlan;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground mb-4">This area is restricted to platform administrators only.</p>
        <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
            <Shield className="h-6 w-6 text-amber-700 dark:text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Admin Controls</h1>
            <p className="text-sm text-muted-foreground">
              {isMaster ? 'Master Account' : 'Admin Account'} — manage subscribers, admin team, legal docs, and platform settings
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ViewAsCustomerToggle />
          <Button asChild variant="outline" size="sm" data-testid="admin-my-settings-btn">
            <a href="/settings">
              <UserCheck className="h-4 w-4 mr-2" />
              My Settings
            </a>
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="admin-legal-docs-btn">
            <a href="/admin/legal">
              <FileText className="h-4 w-4 mr-2" />
              Legal Docs
            </a>
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-500/10">
                  <Building2 className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_tenants}</p>
                  <p className="text-sm text-muted-foreground">Subscribers</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500/10">
                  <Users className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_tenants}</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-violet-500/10">
                  <DollarSign className="h-6 w-6 text-violet-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">${stats.mrr}</p>
                  <p className="text-sm text-muted-foreground">MRR</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-amber-500/10">
                  <TrendingUp className="h-6 w-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">${stats.arr?.toLocaleString() || 0}</p>
                  <p className="text-sm text-muted-foreground">ARR</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Phase F.6 Batch D — Operational widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AIUsageWidget />
        <ComplianceAuditWidget />
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="portal-hierarchy" data-testid="tab-portal-hierarchy">Portal Hierarchy</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="internal-accounts" data-testid="tab-internal-accounts">Internal Accounts</TabsTrigger>
          <TabsTrigger value="licenses" data-testid="tab-licenses">Licenses</TabsTrigger>
          <TabsTrigger value="ip" data-testid="tab-ip">IP &amp; Patents</TabsTrigger>
          <TabsTrigger value="howto" data-testid="tab-howto">How-To</TabsTrigger>
          <TabsTrigger value="test-accounts" data-testid="tab-test-accounts">Test Accounts</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax-overview">Tax</TabsTrigger>
          {isMaster && <TabsTrigger value="admins" data-testid="tab-admins">Admin Accounts</TabsTrigger>}
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics" data-testid="tab-analytics-content">
          <TenantAnalytics />
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <OwnerNotesPanel />
        </TabsContent>

        {/* Portal Hierarchy Tab */}
        <TabsContent value="portal-hierarchy" data-testid="tab-portal-hierarchy-content">
          <PortalHierarchyPanel />
        </TabsContent>

        {/* Internal Accounts Tab */}
        <TabsContent value="internal-accounts">
          <InternalAccountsPanel />
        </TabsContent>

        {/* Test Accounts Tab */}
        <TabsContent value="test-accounts">
          <TestAccountsPanel />
        </TabsContent>

        {/* Licenses Tab */}
        <TabsContent value="licenses" data-testid="tab-licenses-content">
          <LicensesPanel />
        </TabsContent>

        {/* IP & Patents Tab */}
        <TabsContent value="ip" data-testid="tab-ip-content">
          <IPPanel />
        </TabsContent>

        {/* How-To Guide Tab */}
        <TabsContent value="howto" data-testid="tab-howto-content">
          <HowToGuide />
        </TabsContent>

        {/* Subscribers Tab */}
        <TabsContent value="subscribers">
          {/* Phase F.6 Batch D — Bulk subscriber actions panel above the existing roster */}
          <BulkSubscriberActions tenants={filteredTenants} onChanged={fetchData} />
          <Card className="mt-4">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle>Subscriber Accounts</CardTitle>
                  <CardDescription>View and manage all subscriber subscriptions</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      className="pl-9 w-[180px]"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trialing">Trialing</SelectItem>
                      <SelectItem value="past_due">Past Due</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterPlan} onValueChange={setFilterPlan}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                      <SelectItem value="modular">Modular</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Business</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTenants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No subscribers found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTenants.map((tenant) => {
                        const planConfig = PLAN_CONFIG[tenant.subscription_plan] || PLAN_CONFIG.modular;
                        const statusConfig = STATUS_CONFIG[tenant.subscription_status] || {};
                        const StatusIcon = statusConfig.icon || Clock;

                        return (
                          <TableRow key={tenant.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{tenant.name}</p>
                                <p className="text-xs text-muted-foreground">{tenant.slug}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm">{tenant.owner_name || '-'}</p>
                                <p className="text-xs text-muted-foreground">{tenant.owner_email || '-'}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${planConfig.color} text-white`}>
                                {planConfig.label || tenant.subscription_plan}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <StatusIcon
                                  className={`h-3.5 w-3.5 ${
                                    tenant.subscription_status === 'active'
                                      ? 'text-green-500'
                                      : tenant.subscription_status === 'trialing'
                                      ? 'text-blue-500'
                                      : tenant.subscription_status === 'past_due'
                                      ? 'text-amber-500'
                                      : 'text-red-500'
                                  }`}
                                />
                                <span className="text-sm capitalize">{tenant.subscription_status}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {tenant.is_active ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditTenant(tenant)}
                                  title="Edit Subscriber"
                                  data-testid={`edit-tenant-${tenant.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {tenant.is_active ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleQuickAction(tenant.id, 'deactivate')}
                                    title="Deactivate"
                                  >
                                    <PowerOff className="h-4 w-4 text-red-500" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleQuickAction(tenant.id, 'activate')}
                                    title="Activate"
                                  >
                                    <Power className="h-4 w-4 text-green-500" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax & Compliance Tab */}
        <TabsContent value="tax">
          <TaxOverview />
        </TabsContent>

        {/* Admin Accounts Tab */}
        {isMaster && (
          <TabsContent value="admins">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Admin Accounts</CardTitle>
                    <CardDescription>Manage platform administrators</CardDescription>
                  </div>
                  <Button onClick={() => setCreateAdminDialogOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Admin
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {admins.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            No admin accounts created yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        admins.map((admin) => (
                          <TableRow key={admin.id}>
                            <TableCell className="font-medium">{admin.name}</TableCell>
                            <TableCell>{admin.email}</TableCell>
                            <TableCell>
                              <Badge variant={admin.is_active ? 'default' : 'secondary'}>
                                {admin.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteAdmin(admin.id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ============ EDIT SUBSCRIBER DIALOG ============ */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Edit Subscriber: {selectedTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Manage subscription, modules, trial period, and users
            </DialogDescription>
          </DialogHeader>

          {selectedTenant && (
            <Tabs defaultValue="subscription" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="subscription">Subscription</TabsTrigger>
                <TabsTrigger value="modules">Module Access</TabsTrigger>
                <TabsTrigger value="users">Users ({tenantUsers.length})</TabsTrigger>
              </TabsList>

              {/* Subscription Tab */}
              <TabsContent value="subscription" className="flex-1 overflow-y-auto space-y-4 p-1">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Business Name</Label>
                    <Input value={selectedTenant.name} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Owner Email</Label>
                    <Input value={selectedTenant.owner_email || '-'} disabled />
                  </div>
                </div>

                <Separator />

                {/* Subscription Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Subscription Plan</Label>
                    <Select
                      value={editForm.subscription_plan}
                      onValueChange={(value) => setEditForm({ ...editForm, subscription_plan: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">Starter ($14/mo)</SelectItem>
                        <SelectItem value="professional">Professional ($34/mo)</SelectItem>
                        <SelectItem value="enterprise">Enterprise ($69/mo)</SelectItem>
                        <SelectItem value="modular">Modular (Custom)</SelectItem>
                        <SelectItem value="legacy">Legacy (Full Access)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={editForm.subscription_status}
                      onValueChange={(value) => setEditForm({ ...editForm, subscription_status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trialing">Trialing</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="past_due">Past Due</SelectItem>
                        <SelectItem value="canceled">Canceled</SelectItem>
                        <SelectItem value="incomplete">Incomplete</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Trial Days */}
                <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">Trial Period</Label>
                      <p className="text-sm text-muted-foreground">
                        Set or extend the trial period for this subscriber
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="365"
                        className="w-20"
                        value={editForm.trial_days}
                        onChange={(e) =>
                          setEditForm({ ...editForm, trial_days: parseInt(e.target.value) || 0 })
                        }
                      />
                      <span className="text-sm text-muted-foreground">days</span>
                    </div>
                  </div>
                </div>

                {/* Account Active Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Account Active</Label>
                    <p className="text-sm text-muted-foreground">User can access the platform</p>
                  </div>
                  <Switch
                    checked={editForm.is_active}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                  />
                </div>
              </TabsContent>

              {/* Module Access Tab */}
              <TabsContent value="modules" className="flex-1 overflow-y-auto space-y-3 p-1">
                <p className="text-sm text-muted-foreground">
                  Toggle parent modules to enable/disable all child features, or toggle individual features.
                </p>

                {Object.entries(MODULE_HIERARCHY).map(([moduleKey, module]) => {
                  const Icon = module.icon;
                  const isExpanded = expandedModules[moduleKey];
                  const childIds = module.children.map((c) => c.id);
                  const enabledCount = childIds.filter((id) => editForm.enabled_modules?.includes(id)).length;
                  const allEnabled = enabledCount === childIds.length;
                  const someEnabled = enabledCount > 0 && enabledCount < childIds.length;

                  return (
                    <div key={moduleKey} className="border rounded-lg overflow-hidden">
                      {/* Module Header */}
                      <div
                        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 ${
                          allEnabled ? 'bg-green-50 dark:bg-green-950/30' : ''
                        }`}
                        onClick={() => toggleModule(moduleKey)}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <div className={`p-1.5 rounded ${module.color}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium">{module.name}</p>
                            <p className="text-xs text-muted-foreground">{module.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={allEnabled ? 'default' : someEnabled ? 'secondary' : 'outline'}>
                            {enabledCount}/{childIds.length}
                          </Badge>
                          <Switch
                            checked={allEnabled}
                            onCheckedChange={() => toggleParentModule(moduleKey)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Child Features */}
                      {isExpanded && (
                        <div className="border-t bg-muted/20 p-3 space-y-2">
                          {module.children.map((feature) => {
                            const isEnabled = editForm.enabled_modules?.includes(feature.id);
                            return (
                              <div
                                key={feature.id}
                                className="flex items-center justify-between py-1 pl-10"
                              >
                                <span className="text-sm">{feature.name}</span>
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleFeature(feature.id, moduleKey)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users" className="flex-1 overflow-y-auto p-1">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : tenantUsers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No users found for this subscriber</p>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tenantUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">{user.name || '-'}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {user.role?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {user.is_active ? (
                                <Badge className="bg-green-500">Active</Badge>
                              ) : (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteUserDialog(user)}
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTenant} disabled={savingChanges}>
              {savingChanges ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={!!deleteUserDialog} onOpenChange={() => setDeleteUserDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteUserDialog?.email}</strong>? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteUser}>
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Admin Dialog */}
      <Dialog open={createAdminDialogOpen} onOpenChange={setCreateAdminDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Admin Account</DialogTitle>
            <DialogDescription>Add a new platform administrator</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Full Name</Label>
              <Input
                id="admin-name"
                placeholder="John Smith"
                value={newAdminForm.name}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@example.com"
                value={newAdminForm.email}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="Min. 8 characters"
                value={newAdminForm.password}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateAdminDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAdmin}>Create Admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ─── Platform-admin "View as Customer" toggle ───
 * When OFF (default): hides all customer modules in the sidebar — admin only
 * sees their Owner Console. When ON: customer sidebar & /dashboard re-enabled
 * so the admin can walk through the customer experience.
 */
function ViewAsCustomerToggle() {
  const { user, adminViewAsCustomer, setAdminViewAsCustomer } = useAuth();
  const navigate = useNavigate();
  if (!user?.is_platform_admin) return null;
  const handleToggle = () => {
    const next = !adminViewAsCustomer;
    setAdminViewAsCustomer(next);
    if (next) {
      navigate("/dashboard");
    }
  };
  return (
    <Button
      onClick={handleToggle}
      variant={adminViewAsCustomer ? "default" : "outline"}
      size="sm"
      data-testid="admin-view-as-customer-toggle"
      title={adminViewAsCustomer ? "Return to Admin-only view" : "Open the customer view (Quotes / Clients / Calendar etc.)"}
    >
      <Eye className="h-4 w-4 mr-2" />
      {adminViewAsCustomer ? "Customer View ON" : "View as Customer"}
    </Button>
  );
}
